import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

/**
 * Historique de navigation interne à l'admin (par onglet, en sessionStorage).
 * Sert à proposer un lien « Retour » vers la page d'où l'on vient réellement
 * (ex. profil utilisateur → parcours → « ← Camille Lefèvre ») plutôt qu'un
 * retour figé vers la liste.
 */
export type NavEntry = {
  key: string;
  pathname: string;
  path: string; // pathname + search
  label?: string;
};

type NavHistoryState = {
  entries: NavEntry[];
  record: (entry: NavEntry, type: 'PUSH' | 'POP' | 'REPLACE') => void;
  setLabel: (key: string, label: string) => void;
};

const MAX_ENTRIES = 50;

export const useNavHistoryStore = create<NavHistoryState>()(
  persist(
    (set) => ({
      entries: [],
      record: (entry, type) =>
        set(({ entries }) => {
          const idx = entries.findIndex((e) => e.key === entry.key);
          if (idx !== -1) {
            // Retour arrière / avant ou simple re-rendu : on se repositionne sur l'entrée connue
            const kept = entries.slice(0, idx + 1);
            kept[idx] = { ...entries[idx], pathname: entry.pathname, path: entry.path };
            return { entries: kept };
          }
          if (type === 'REPLACE' && entries.length > 0) {
            const prev = entries[entries.length - 1];
            const label = prev.pathname === entry.pathname ? prev.label : undefined;
            return { entries: [...entries.slice(0, -1), { ...entry, label }] };
          }
          if (type === 'POP') {
            // Entrée inconnue (nouvel onglet, rechargement sans historique) : on repart de zéro
            return { entries: [entry] };
          }
          return { entries: [...entries, entry].slice(-MAX_ENTRIES) };
        }),
      setLabel: (key, label) =>
        set(({ entries }) => ({
          entries: entries.map((e) => (e.key === key && e.label !== label ? { ...e, label } : e)),
        })),
    }),
    { name: 'runnrz-admin-nav', storage: createJSONStorage(() => sessionStorage) }
  )
);

/** À monter une fois sous le routeur : enregistre chaque changement de page. */
export function useTrackNavigation() {
  const location = useLocation();
  const type = useNavigationType();
  const record = useNavHistoryStore((s) => s.record);
  useEffect(() => {
    record(
      { key: location.key, pathname: location.pathname, path: location.pathname + location.search },
      type
    );
  }, [location.key, location.pathname, location.search, type, record]);
}

/** Nom lisible de la page courante, affiché dans le lien retour des pages suivantes. */
export function usePageLabel(label: string | null | undefined) {
  const location = useLocation();
  const setLabel = useNavHistoryStore((s) => s.setLabel);
  const entries = useNavHistoryStore((s) => s.entries);
  const known = entries.some((e) => e.key === location.key);
  useEffect(() => {
    if (label && known) setLabel(location.key, label);
  }, [label, known, location.key, setLabel]);
}

// Libellés par défaut quand la page précédente n'a pas déclaré de nom
const ROUTE_LABELS: [RegExp, string][] = [
  [/^\/utilisateurs\/\d+/, 'Profil utilisateur'],
  [/^\/utilisateurs/, 'Utilisateurs'],
  [/^\/parcours\/compare/, 'Comparaison de parcours'],
  [/^\/parcours-v2\/force-finish/, 'Parcours V2 bloqués'],
  [/^\/parcours-v2\/\d+/, 'Parcours V2'],
  [/^\/parcours-v2/, 'Parcours V2'],
  [/^\/parcours\/\d+/, 'Parcours'],
  [/^\/parcours/, 'Parcours'],
  [/^\/challenges\/\d+\/participants/, 'Participants'],
  [/^\/challenges\/\d+\/leaderboard/, 'Classement'],
  [/^\/challenges\/add/, 'Nouveau défi'],
  [/^\/challenges\/\d+/, 'Défi'],
  [/^\/challenges/, 'Défis'],
  [/^\/sponsors\/\d+/, 'Sponsor'],
  [/^\/sponsors/, 'Sponsors'],
  [/^\/entities\/\d+\/sites/, 'Sites'],
  [/^\/entities\/\d+/, 'Entité'],
  [/^\/entities/, 'Entités'],
  [/^\/short-links\/\d+/, 'Lien court'],
  [/^\/short-links/, 'Liens courts'],
  [/^\/signalements\/\d+/, 'Alerte'],
  [/^\/signalements/, 'Alertes'],
  [/^\/reports/, 'Signalements'],
  [/^\/kpi\/segments/, 'Premium & hard users'],
  [/^\/kpi/, 'KPI mensuels'],
  [/^\/dashboard/, 'Tableau de bord'],
  [/^\/map-cells/, 'Grille du réseau'],
  [/^\/map/, 'Carte des exclusions'],
  [/^\/pois/, 'Points'],
  [/^\/poi-zones/, 'Zones'],
  [/^\/poi-categories/, 'Catégories'],
  [/^\/languages/, 'Langues'],
  [/^\/simulator/, 'Simulateur'],
  [/^\/revenuecat/, 'Réconciliation RC'],
  [/^\/account/, 'Sécurité'],
  [/^\/$/, 'Tableau de bord'],
];

export const labelForPath = (pathname: string) =>
  ROUTE_LABELS.find(([re]) => re.test(pathname))?.[1] ?? 'Retour';

/**
 * Cible du lien « Retour » : la dernière page visitée (différente de la page courante),
 * sinon le repli fourni (la liste parente). `go()` utilise l'historique du navigateur
 * quand c'est possible pour retrouver filtres, pagination et position.
 */
export function useBackLink(fallbackPath: string, fallbackLabel?: string) {
  const location = useLocation();
  const navigate = useNavigate();
  const entries = useNavHistoryStore((s) => s.entries);

  const currentIdx = entries.findIndex((e) => e.key === location.key);
  let target: NavEntry | null = null;
  let distance = 0;
  if (currentIdx > 0) {
    for (let i = currentIdx - 1; i >= 0; i--) {
      if (entries[i].pathname !== location.pathname) {
        target = entries[i];
        distance = currentIdx - i;
        break;
      }
    }
  }

  const label = target ? target.label || labelForPath(target.pathname) : fallbackLabel || labelForPath(fallbackPath);

  const go = useCallback(() => {
    if (target && distance > 0 && window.history.length > distance) {
      navigate(-distance);
    } else {
      navigate(fallbackPath);
    }
  }, [target, distance, navigate, fallbackPath]);

  return { label, go, fromHistory: !!target, path: target?.path ?? fallbackPath };
}
